import { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { PENDING_IB_LAB_ORDER_KEY } from "features/labOrders/constants";
import { useDownloadLabOrder } from "features/labOrders/hooks/useDownloadLabOrder";
import {
  hasPendingIbReturn,
  pollLabOrderDownload,
  readIbReturnMarkers,
  resolveIbLabOrderId,
} from "features/labOrders/utils/ibLabOrderReturn";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";

// On return from the Healthlink Integrated Browser, confirm that Healthlink holds
// the completed order and refresh the list — keeping the list hidden until it
// settles so an outdated one is never shown. The marker reading, id resolution and
// polling live in features/labOrders/utils/ibLabOrderReturn.
export const useIbLabOrderReturn = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const patientId = useCurrentPatientId();
  const tenantVal = localStorage.getItem("kinesin-tenant");
  const { mutateAsync: downloadLabOrder } = useDownloadLabOrder();

  // Seeded from the markers so the loader is up on first paint, and cleared once
  // the return has been processed.
  const [isProcessingReturn, setIsProcessingReturn] = useState(() =>
    hasPendingIbReturn(readIbReturnMarkers(searchParams))
  );

  // One confirmation per return, whatever remounts. React StrictMode double-invokes
  // effects in development, which showed as every toast appearing twice and two
  // download polls racing for the same order (reported 2026-08-07). A ref rather than
  // state because it must not itself trigger a render.
  const hasConfirmed = useRef(false);

  useEffect(() => {
    const markers = readIbReturnMarkers(searchParams);

    if (!hasPendingIbReturn(markers) || hasConfirmed.current) return;

    hasConfirmed.current = true;

    const clearReturnMarkers = () => {
      sessionStorage.removeItem(PENDING_IB_LAB_ORDER_KEY);
      setSearchParams((previous) => {
        previous.delete("labOrderId");
        previous.delete("controlId");

        return previous;
      });
    };

    const confirmReturn = async () => {
      try {
        const labOrderId = await resolveIbLabOrderId({
          ...markers,
          patientId,
          tenantVal,
        });

        if (!labOrderId) {
          toast.error(
            "We couldn't match the Healthlink lab order to update its status. Please check the order in the list below."
          );
        } else if (await pollLabOrderDownload(downloadLabOrder, labOrderId)) {
          toast.success("Lab order submitted to Healthlink successfully!");
        } else {
          toast.error(
            "Healthlink hasn't confirmed the completed lab order yet. Open the order and check again in a few seconds."
          );
        }

        // Drop the cached list so the table refetches once revealed — the GP never
        // sees the pre-processing state.
        queryClient.removeQueries({ queryKey: ["labOrdersList"] });
      } finally {
        clearReturnMarkers();
        setIsProcessingReturn(false);
      }
    };

    confirmReturn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isProcessingReturn };
};
